import { HEALTHY_INTERVAL_MS } from "../core/suppression.ts";
import type { BlinkEvent } from "../core/types.ts";
import { FrameClock, type Mechanic, type Readout, relief } from "./mechanic.ts";

/**
 * Suppression as *vision*, and the clearest demonstration of design doc §5
 * available: partial blink, partial effect, with nothing in between to argue
 * about.
 *
 * The screen darkens continuously. Every blink lifts the dark in proportion to
 * how far the eyelid actually closed — a half blink literally half-lights the
 * scene. Nobody has to read a tutorial saying "close your eyes fully"; you can
 * see that you did not.
 *
 * ## Is this "rewarding blinking"?
 *
 * It is the one place where the §1 inversion needs care, so: the light is
 * clamped at full and decays on a fixed clock, which means blinking faster than
 * your natural rate buys **nothing** — you cannot bank light, and a second
 * blink one second after the first adds almost none because there is almost
 * none missing. What it does is make blinking *at* the natural rate feel like
 * nothing is happening, and suppression feel like the room going dark. Which is
 * exactly what a tear film does, and the metaphor teaches itself.
 */

/**
 * Exponential decay constant: one healthy blink interval is one e-fold.
 *
 * Tying it to `HEALTHY_INTERVAL_MS` rather than picking a number makes the
 * whole mechanic legible in one line — go a healthy interval without blinking
 * and you are at 37% light; go three and you are at 5%.
 *
 * The first version used 2600ms, and replaying the recorded lantern session
 * through it showed why that was too aggressive: median light 0.42, below the
 * "dim" alert for **33% of the session**, with one unbroken 16.5-second dark
 * stretch. Most of that came from detection gaps — face loss and closures the
 * detector abandoned — not from the player failing to blink. Punishing someone
 * that hard for our own missed blinks makes the mechanic a report on the
 * detector rather than on their eyes.
 */
const TAU_MS = HEALTHY_INTERVAL_MS;

/**
 * The scrim never reaches fully opaque.
 *
 * Total darkness is the correct penalty and a bad idea: someone who cannot see
 * the screen at all cannot find their way out of the mechanic, and on a phone
 * that reads as a crash rather than as feedback.
 */
const MAX_SCRIM = 0.88;

/** Below this the readout goes red. */
const DIM = 0.25;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export class Lantern implements Mechanic {
  readonly id = "lantern" as const;
  readonly hint = "blink to see · a half blink gives you half the light";

  #clock = new FrameClock();
  #light = 1;

  constructor(private readonly scrim: HTMLElement) {}

  /**
   * Adds light rather than setting it, so a partial blink out of darkness gets
   * you partway rather than to a fixed level — two half blinks are worth about
   * one full one, which is the honest arithmetic.
   *
   * `kind` is not read. Nothing here needs to know which side of an unvalidated
   * threshold the blink fell on.
   */
  onBlink(event: BlinkEvent): void {
    this.#light = clamp01(this.#light + relief(event.amplitude));
  }

  update(t: number): void {
    const dt = this.#clock.step(t);
    if (dt > 0) this.#light *= Math.exp(-dt / TAU_MS);

    this.scrim.style.opacity = ((1 - this.#light) * MAX_SCRIM).toFixed(3);
  }

  readout(): Readout {
    const dim = this.#light < DIM;
    return {
      value: `${Math.round(this.#light * 100)}%`,
      label: "light",
      ...(dim ? { alert: true } : {}),
    };
  }

  get light(): number {
    return this.#light;
  }

  reset(): void {
    this.#clock.reset();
    this.#light = 1;
    this.scrim.style.opacity = "";
  }
}

export { MAX_SCRIM, TAU_MS };
